/**
 * Meal Plan List Page Component
 *
 * Displays a list of meal plans for dietitians. This page is accessible when the
 * Meal Plan Generator Card is clicked from the Dietitian Dashboard.
 * Uses the Layout component for consistent page structure and theming.
 *
 * @module components/pages/MealPlanListPage
 */

import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { MealPlansList } from '@/components/meal-plans/MealPlansList';
import { useMealPlans } from '@/hooks/useMealPlans';
import type { MealPlansFilterState } from '@/hooks/useMealPlans';
import { BackButton } from '../ui/BackButton';

/**
 * MealPlanListPage Component Props
 */
export interface MealPlanListPageProps {
  className?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  fullWidth?: boolean;
}

/**
 * Main Meal Plan List Page Component
 *
 * Renders the meal plans list within the standard Layout component,
 * following the same pattern as other pages in the BiteWise platform.
 */
export const MealPlanListPage: React.FC<MealPlanListPageProps> = ({
  className,
  showHeader = true,
  showFooter = true,
  fullWidth = false,
}) => {
  // Filter state
  const [filterState, setFilterState] = useState<MealPlansFilterState>({
    page: 1,
    pageSize: 10,
  });

  // Use the meal plans hook
  const { mealPlans, pagination, loading, error, refresh, deleteMealPlan } = useMealPlans(filterState);

  // Handle page change
  const handlePageChange = (page: number) => {
    setFilterState(prev => ({
      ...prev,
      page,
    }));
  };

  return (
    <Layout
      className={className}
      showHeader={showHeader}
      showFooter={showFooter}
      fullWidth={fullWidth}
      mainClassName=""
    >
      <div className="min-h-screen py-8 px-4 ">
        <div className="max-w-4xl mx-auto px-4 space-y-6">
          <div className="flex items-center gap-4 mb-4">
            <BackButton fallback='/dashboard'/>
          </div>

          <MealPlansList
            mealPlans={mealPlans}
            pagination={pagination}
            loading={loading}
            error={error}
            refresh={refresh}
            onPageChange={handlePageChange}
            deleteMealPlan={deleteMealPlan}
          />
        </div>
      </div>
    </Layout>
  );
};

export default MealPlanListPage;